import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Trash2,
  X,
  Play,
  Edit2,
  ChevronRight,
  Layers,
  Dumbbell,
  Save,
} from 'lucide-react';
import { programsApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { TrainingProgram, ProgramTemplate } from '@/types';

export function ProgramDetailPage() {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [deleteTemplateConfirm, setDeleteTemplateConfirm] = useState<string | null>(null);

  // Hae ohjelma
  const { data: program, isLoading, error } = useQuery({
    queryKey: ['program', programId],
    queryFn: () => programsApi.get(programId!),
    enabled: !!programId,
  });

  // Päivitä ohjelma
  const updateProgramMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string }) =>
      programsApi.update(programId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      setIsEditing(false);
    },
  });

  // Luo treenipohja
  const createTemplateMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      programsApi.createTemplate(programId!, data),
    onSuccess: (template) => {
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
      setShowCreateTemplateModal(false);
      setNewTemplateName('');
      // Siirry pohjan muokkaukseen
      navigate(`/programs/${programId}/templates/${template.id}`);
    },
  });

  // Poista treenipohja
  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: string) =>
      programsApi.deleteTemplate(programId!, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
      setDeleteTemplateConfirm(null);
    },
  });

  const handleStartEditing = () => {
    if (program) {
      setEditName(program.name);
      setEditDescription(program.description || '');
      setIsEditing(true);
    }
  };

  const handleSaveEdit = () => {
    if (editName.trim()) {
      updateProgramMutation.mutate({
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleCreateTemplateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTemplateName.trim()) {
      createTemplateMutation.mutate({ name: newTemplateName.trim() });
    }
  };

  const handleDeleteTemplateClick = (templateId: string, e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTemplateConfirm(templateId);
  };

  const handleConfirmDeleteTemplate = async () => {
    if (deleteTemplateConfirm) {
      await deleteTemplateMutation.mutateAsync(deleteTemplateConfirm);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !program) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Ohjelmaa ei löytynyt</p>
        <Link to="/programs" className="btn btn-secondary mt-4">
          Takaisin ohjelmiin
        </Link>
      </div>
    );
  }

  const templates = program.templates || [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Navigaatio */}
      <div className="mb-6">
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Takaisin ohjelmiin
        </Link>
      </div>

      {/* Ohjelman tiedot */}
      <div className="card p-5 mb-6">
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="edit-program-name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Ohjelman nimi
              </label>
              <input
                type="text"
                id="edit-program-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="input w-full"
                autoFocus
              />
            </div>
            <div>
              <label
                htmlFor="edit-program-description"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Kuvaus
              </label>
              <textarea
                id="edit-program-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                className="input w-full resize-none"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCancelEdit}
                className="btn btn-secondary"
              >
                Peruuta
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editName.trim() || updateProgramMutation.isPending}
                className="btn btn-primary"
              >
                <Save className="h-4 w-4" />
                {updateProgramMutation.isPending ? 'Tallennetaan...' : 'Tallenna'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {program.name}
                </h1>
                {program.isActive && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                    Aktiivinen
                  </span>
                )}
              </div>
              {program.description && (
                <p className="text-gray-600 dark:text-gray-400">
                  {program.description}
                </p>
              )}
            </div>
            <button
              onClick={handleStartEditing}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Muokkaa"
            >
              <Edit2 className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Treenipohjat */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Layers className="h-5 w-5 text-primary-500" />
          Treenipohjat
        </h2>
        <button
          onClick={() => setShowCreateTemplateModal(true)}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Lisää pohja</span>
        </button>
      </div>

      {templates.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-5xl mb-4">📝</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Ei vielä treenipohjia
          </h3>
          <p className="text-gray-500 mb-4">
            Lisää ensimmäinen treenipohja ohjelmaan
          </p>
          <button
            onClick={() => setShowCreateTemplateModal(true)}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4" />
            Lisää treenipohja
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((template: ProgramTemplate) => (
            <TemplateCard
              key={template.id}
              programId={programId!}
              template={template}
              onDelete={handleDeleteTemplateClick}
            />
          ))}
        </div>
      )}

      {/* Luo pohja -modaali */}
      {showCreateTemplateModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowCreateTemplateModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Uusi treenipohja
              </h3>
              <button
                onClick={() => setShowCreateTemplateModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateTemplateSubmit}>
              <div>
                <label
                  htmlFor="template-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Pohjan nimi *
                </label>
                <input
                  type="text"
                  id="template-name"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="esim. Päivä A - Alakroppa"
                  className="input w-full"
                  autoFocus
                  required
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateTemplateModal(false)}
                  className="flex-1 btn btn-secondary"
                >
                  Peruuta
                </button>
                <button
                  type="submit"
                  disabled={!newTemplateName.trim() || createTemplateMutation.isPending}
                  className="flex-1 btn btn-primary"
                >
                  {createTemplateMutation.isPending ? 'Luodaan...' : 'Luo pohja'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Poista pohja -modaali */}
      {deleteTemplateConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteTemplateConfirm(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Poista treenipohja?
              </h3>
              <button
                onClick={() => setDeleteTemplateConfirm(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Haluatko varmasti poistaa tämän treenipohjan? Toimintoa ei voi perua.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTemplateConfirm(null)}
                className="flex-1 btn btn-secondary"
              >
                Peruuta
              </button>
              <button
                onClick={handleConfirmDeleteTemplate}
                disabled={deleteTemplateMutation.isPending}
                className="flex-1 btn bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteTemplateMutation.isPending ? 'Poistetaan...' : 'Poista'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Treenipohjkortti
function TemplateCard({
  programId,
  template,
  onDelete,
}: {
  programId: string;
  template: ProgramTemplate;
  onDelete: (templateId: string, e: React.MouseEvent | React.TouchEvent) => void;
}) {
  const exerciseCount = template.exercises?.length ?? 0;
  const workoutCount = template._count?.workouts ?? 0;

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {/* Nimi */}
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
              {template.name}
            </h3>

            {/* Liikkeiden ja treenien määrä */}
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1.5">
                <Dumbbell className="h-4 w-4" />
                <span>
                  {exerciseCount} {exerciseCount === 1 ? 'liike' : 'liikettä'}
                </span>
              </div>
              {workoutCount > 0 && (
                <span>
                  {workoutCount} {workoutCount === 1 ? 'treeni' : 'treeniä'} tehty
                </span>
              )}
            </div>

            {/* Muistiinpanot */}
            {template.notes && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                {template.notes}
              </p>
            )}
          </div>

          {/* Toiminnot */}
          <div className="flex items-center gap-1 ml-4">
            {/* Aloita treeni */}
            <Link
              to={`/programs/${programId}/templates/${template.id}/start`}
              className="p-3 text-primary-500 hover:text-primary-600 active:text-primary-700 transition-colors touch-manipulation"
              title="Aloita treeni"
              onClick={(e) => e.stopPropagation()}
            >
              <Play className="h-5 w-5" />
            </Link>

            {/* Muokkaa */}
            <Link
              to={`/programs/${programId}/templates/${template.id}`}
              className="p-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors touch-manipulation"
              title="Muokkaa"
              onClick={(e) => e.stopPropagation()}
            >
              <Edit2 className="h-5 w-5" />
            </Link>

            {/* Poista */}
            <button
              type="button"
              onClick={(e) => onDelete(template.id, e)}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(template.id, e);
              }}
              className="p-3 text-gray-400 hover:text-red-500 active:text-red-600 transition-colors touch-manipulation"
              title="Poista"
            >
              <Trash2 className="h-5 w-5" />
            </button>

            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </div>
      </div>
    </div>
  );
}
